import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai'; // 👈 Add this
import { supabase } from './supabaseClient';
import { useAuth, setSingleSession } from './AuthContext';
import './AuthForm.css';

function Login() {
  const navigate = useNavigate();
  const { error: authError, setError: setAuthError } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // 👈 Toggle password visibility

  useEffect(() => {
    document.title = "Login";
  }, []);

  useEffect(() => {
    if (authError) {
      setError(authError);
      setAuthError('');
    }
    const kickedMsg = sessionStorage.getItem('session_kicked');
    if (kickedMsg) {
      setError(kickedMsg);
    }
  }, [authError, setAuthError]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { error: signInError, data } = await supabase.auth.signInWithPassword({
        email: form.email,
        password: form.password
      });

      if (signInError) {
        setError(signInError.message);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', data.user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        setError('Error checking profile status');
        return;
      }

      if (profile && profile.role === 'resident') {
        setError('Only admin and barangay users can log in. Residents are not allowed to access this portal.');
        return;
      }

      if (!profile) {
        await setSingleSession(data.user.id);
        navigate('/profile');
      } else {
        await setSingleSession(data.user.id);
        await supabase.from('audit_logs').insert([
          {
            user_id: profile.id,
            action: 'user login',
            target_id: profile.id,
            target_type: 'Login',
            details: { email: data.user.email, timestamp: new Date().toISOString() }
          }
        ]);
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <button 
        className="back-button"
        onClick={() => navigate('/')}
        aria-label="Go back to homepage"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Back</span>
      </button>

      <div className="auth-split-bg">
        <div className="auth-split-left">
          <form className="login-form" onSubmit={handleLogin}>
            <div className="auth-title">Welcome Back</div>

            <div className="input-group">
              <input
                className="auth-input"
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>

            {/* PASSWORD FIELD WITH REACT ICONS */}
            <div className="input-group" style={{ position: 'relative' }}>
              <input
                className="auth-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                style={{ paddingRight: '40px' }}
              />
              <div
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  color: '#555',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}
                title={showPassword ? 'Hide Password' : 'Show Password'}
              >
                {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
              </div>
            </div>

            <button className="auth-btn" type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>

            {error && (
              <div className="error-message">
                {error}
                {sessionStorage.getItem('session_kicked') && (
                  <button
                    style={{
                      marginLeft: 12,
                      padding: '2px 8px',
                      borderRadius: 4,
                      border: 'none',
                      background: '#eee',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setError('');
                      sessionStorage.removeItem('session_kicked');
                    }}
                  >
                    Dismiss
                  </button>
                )}
              </div>
            )}

            <div className="auth-footer">
              <b>Note:</b> Only <span className="admin-text">admin</span> and <span className="barangay-text">barangay</span> users can log in.<br />
              Residents are not allowed to access this portal.
            </div>
          </form>
        </div>

        <div className="auth-split-right" style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100%',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'url(/login-image.png) center center/cover no-repeat',
            filter: 'blur(7px)',
            zIndex: 1,
          }} />
          <div className="auth-hero-content" style={{ padding: 32, maxWidth: 400, margin: 32, zIndex: 2, position: 'relative' }}>
            <h2>Effortlessly manage disaster response and community safety.</h2>
            <p>Log in to access your GabaySakuna dashboard and help your community stay safe and informed.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
